import { useContext, useState } from 'react';

import { CorePermissionToggle, Permissions } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import { PermissionOption } from '@/core/users/UserPermissions/ProductPermissionRow';

const NO_ACCESS_LABEL = 'Disabled';

export enum PermissionError {
  ROADMAP_RW_IDEAS_REQUIRED = 'Ideas must be enabled to enable the internal roadmap.',
  IDEAS_NO_ROADMAP_REQUIRED = 'You cannot view internal roadmaps if you disable ideas.',
}

type PermissionObject<K extends string> = {
  [key in K]?: PermissionOption;
}
type UsePermissions = (currentPermissions: Permissions) => {
  error?: PermissionError;
  setError: (error?: PermissionError) => void;
  core: {
    [key in keyof CorePermissionToggle]: PermissionObject<CorePermissionToggle[key]>;
  }
}
export const usePermissions: UsePermissions = (currentPermissions) => {
  const { hasAccessTo } = useContext(AuthContext);
  const [error, setError] = useState<PermissionError>();

  return {
    error,
    setError,
    core: {
      features: {
        rw: {
          label: hasAccessTo('internal_roadmap') ? 'Can view & edit roadmap content' : 'Can view & edit features',
          isSelected: currentPermissions.core?.features === 'rw',
          validators: [
            {
              isValid: error !== PermissionError.ROADMAP_RW_IDEAS_REQUIRED,
              error: PermissionError.ROADMAP_RW_IDEAS_REQUIRED,
            },
          ],
        },
        no: {
          label: NO_ACCESS_LABEL,
          isSelected: currentPermissions.core?.features === 'no',
        },
      },
      feedback: {
        rw: {
          label: 'Can view & edit ideas',
          isSelected: currentPermissions.core?.feedback === 'rw',
        },
        'r+': {
          label: 'View-only permission',
          isSelected: currentPermissions.core?.feedback === 'r+',
        },
        no: {
          label: NO_ACCESS_LABEL,
          isSelected: currentPermissions.core?.feedback === 'no',
          validators: [
            {
              isValid: error !== PermissionError.IDEAS_NO_ROADMAP_REQUIRED,
              error: PermissionError.IDEAS_NO_ROADMAP_REQUIRED,
            },
          ],
        },
      },
      knowledgebase: {
        rw: {
          label: 'Can manage & update articles',
          isSelected: currentPermissions.core?.knowledgebase === 'rw',
        },
        ro: {
          label: 'View-only permission',
          isSelected: currentPermissions.core?.knowledgebase === 'ro',
        },
        no: {
          label: NO_ACCESS_LABEL,
          isSelected: currentPermissions.core?.knowledgebase === 'no',
        },
      },
      tickets: {
        rw: {
          label: 'Can manage & update ticketing system',
          isSelected: currentPermissions.core?.tickets === 'rw',
        },
        ro: {
          label: 'View & add private notes',
          isSelected: currentPermissions.core?.tickets === 'ro',
        },
        no: {
          label: NO_ACCESS_LABEL,
          isSelected: currentPermissions.core?.tickets === 'no',
        },
      },
      settings: {
        billing: {
          label: 'Can manage & update settings',
          helperLabel: 'Includes Permissions & Billing',
          isSelected: currentPermissions.core?.settings === 'billing',
        },
        rw: {
          label: 'Can edit account settings only',
          isSelected: currentPermissions.core?.settings === 'rw',
        },
        none: {
          label: NO_ACCESS_LABEL,
          isSelected: currentPermissions.core?.settings === 'none',
        },
      },
    },
  };
};
